import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dropdown,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Option,
  Spinner,
  Switch,
  Textarea
} from '@fluentui/react-components';
import { DocumentAdd20Regular, DocumentSearch20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { GuideDrawer, GuideDrawerForm, GuideDrawerSection } from '../../../../theme/components/GuideDrawer';
import { GuideField, GuideHelpText } from '../../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../../theme/components/GuideMessageBar';
import { GuideEmptyState } from '../../../../theme/components/GuideEmptyState';
import { GuideProgress } from '../../../../theme/components/GuideProgress';
import { GuideLinkButton } from '../../../../theme/components/GuideButtons';
import { GuideSkeleton } from '../../../../theme/components/GuideSkeleton';
import {
  ALL_REVIEW_SCOPES,
  ContractReview,
  ContractReviewMode,
  ContractReviewScope,
  MAX_REVIEW_INSTRUCTIONS,
  NewContractReview
} from '../../../../models/Entities/Contracts/ContractReview';
import { ContractAddendum } from '../../../../models/Entities/Contracts/ContractAddendum';
import { FileUpload } from '../../../../models/Entities/Contracts/FileUpload';
import { ErrorType } from '../../../../models/HttpError';
import { errorText, formatDateTime, getDefaultBaseAddendum, isReviewActive, isReviewableAddendum } from '../ContractReview.Functions';
import { useContractReviewStyles } from '../ContractReview.Styles';
import { ReviewStatusPill, TriggerPill } from '../ContractReviewPills/ContractReviewPills.Component';
import { isAfterImportWithoutChanges } from '../ContractReviewStatus/ContractReviewStatus.Functions';

// Drawer de la revisión con IA (US1, US2, D-7), según la guía (GuideDrawer): el botón "Revisar con IA" / "Nueva adenda
// con IA" lo abre con la adenda (revisada o base), los archivos, los alcances y las revisiones anteriores del modo.

export interface LauncherInitialValues {
  fileIds?: string[];
  scopes?: ContractReviewScope[];
}

export interface ContractReviewLauncherProps {
  mode: ContractReviewMode;
  contractId: number;
  /** Contexto del encabezado (número de contrato). */
  contractNumber?: string;
  /** Modo Review: la adenda revisada (sin adenda = todo el contrato). */
  addendum?: ContractAddendum;
  /** Las adendas del contrato: base en NewAddendum y nombres en el historial. */
  addendums?: ContractAddendum[];
  files: FileUpload[];
  isLoadingFiles: boolean;
  isCreating: boolean;
  successCreating: boolean | undefined;
  createError?: ErrorType;
  /** Revisión en curso para la misma adenda (para "Ver revisión" ante el 409). */
  activeReviewId?: string;
  /** Revisiones del contrato (la sección "Revisiones anteriores" muestra las del modo). */
  reviews?: ContractReview[];
  isLoadingReviews?: boolean;
  reviewsError?: ErrorType;
  onLoadFiles: () => void;
  onReloadReviews?: () => void;
  onCreate: (command: NewContractReview) => void;
  onViewReview: (reviewId: string) => void;
  onClearCreateStatus: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  initialValues?: LauncherInitialValues;
  showTrigger?: boolean;
}

/** PDF, Word o Excel por extensión o tipo de contenido: los formatos que IA.API 004 lee en la revisión. */
const REVIEWABLE_EXTENSION = /(^|\.)(pdf|docx?|xlsx?)$/i;
const REVIEWABLE_CONTENT_TYPE =
  /application\/(pdf|msword|vnd\.openxmlformats-officedocument\.(wordprocessingml\.document|spreadsheetml\.sheet)|vnd\.ms-excel)/i;

export const isReviewableFile = (file: Pick<FileUpload, 'fileExtension' | 'contentType' | 'fileName'>): boolean =>
  REVIEWABLE_EXTENSION.test(file.fileExtension ?? '') ||
  REVIEWABLE_CONTENT_TYPE.test(file.contentType ?? '') ||
  REVIEWABLE_EXTENSION.test(file.fileName ?? '');

export const ContractReviewLauncher: React.FC<ContractReviewLauncherProps> = props => {
  const { t } = useTranslation();
  const styles = useContractReviewStyles();
  const isNewAddendum = props.mode === 'NewAddendum';

  const [internalOpen, setInternalOpen] = useState(false);
  const open = props.open ?? internalOpen;
  const setOpen = (value: boolean) => {
    if (props.onOpenChange) props.onOpenChange(value);
    else setInternalOpen(value);
  };

  const reviewable = useMemo(() => props.files.filter(isReviewableFile), [props.files]);
  const baseCandidates = useMemo(() => (props.addendums ?? []).filter(isReviewableAddendum), [props.addendums]);
  const history = useMemo(() => (props.reviews ?? []).filter(r => r.mode === props.mode), [props.reviews, props.mode]);

  const [fileIds, setFileIds] = useState<string[]>([]);
  const [scopes, setScopes] = useState<ContractReviewScope[]>(ALL_REVIEW_SCOPES);
  const [baseAddendumId, setBaseAddendumId] = useState<number | undefined>(undefined);
  // Contracts 021 §3.1: indicaciones para la IA y aplicación automática (activa por defecto)
  const [instructions, setInstructions] = useState('');
  const [autoApply, setAutoApply] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [retryOf, setRetryOf] = useState<ContractReview | undefined>(undefined);

  // Al abrir: archivos, revisiones, valores iniciales (todos los archivos en Review, ninguno en NewAddendum) y base.
  useEffect(() => {
    if (!open) return;
    props.onClearCreateStatus();
    setSubmitted(false);
    setRetryOf(undefined);
    if (reviewable.length === 0) props.onLoadFiles();
    props.onReloadReviews?.();
    setScopes(props.initialValues?.scopes?.length ? props.initialValues.scopes : ALL_REVIEW_SCOPES);
    setBaseAddendumId(getDefaultBaseAddendum(props.addendums)?.id);
    setInstructions('');
    setAutoApply(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || retryOf) return;
    const initial = props.initialValues?.fileIds;
    if (initial?.length) setFileIds(initial.filter(id => reviewable.some(f => f.fileId === id)));
    else setFileIds(isNewAddendum ? [] : reviewable.map(f => f.fileId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reviewable]);

  // Alta exitosa: se cierra el drawer.
  useEffect(() => {
    if (open && submitted && props.successCreating) {
      setSubmitted(false);
      setOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.successCreating]);

  const toggle = <T,>(list: T[], value: T, checked: boolean): T[] =>
    checked ? Array.from(new Set([...list, value])) : list.filter(v => v !== value);

  const disabledReason =
    fileIds.length === 0
      ? t('contractReview.launcher.disabledNoFiles')
      : scopes.length === 0
        ? t('contractReview.launcher.disabledNoScopes')
        : isNewAddendum && baseAddendumId === undefined
          ? t('contractReview.launcher.newAddendum.noBase')
          : instructions.trim().length > MAX_REVIEW_INSTRUCTIONS
            ? t('contractReview.instructions.tooLong', { max: MAX_REVIEW_INSTRUCTIONS })
            : undefined;

  const submit = () => {
    if (disabledReason || props.isCreating) return;
    setSubmitted(true);
    const options = { fileIds, scopes, autoApply, instructions: instructions.trim() || null };
    props.onCreate(
      isNewAddendum
        ? { mode: 'NewAddendum', baseAddendumId: baseAddendumId!, ...options }
        : { mode: 'Review', addendumId: props.addendum?.id ?? null, ...options }
    );
  };

  // "Reintentar": la revisión fallida carga sus archivos y alcances (y la base en NewAddendum) en el formulario.
  const currentAddendumId = props.addendum?.id ?? null;
  const canRetry = (review: ContractReview) =>
    review.status === 'Failed' && (isNewAddendum ? review.baseAddendumId !== null : review.addendumId === currentAddendumId);
  const retry = (review: ContractReview) => {
    setRetryOf(review);
    setFileIds(review.fileIds.filter(id => reviewable.some(f => f.fileId === id)));
    setScopes(review.scopes.length ? review.scopes : ALL_REVIEW_SCOPES);
    if (isNewAddendum && review.baseAddendumId !== null) setBaseAddendumId(review.baseAddendumId);
    props.onClearCreateStatus();
    setSubmitted(false);
  };

  const error = submitted ? props.createError : undefined;
  const errorMessage =
    error?.ErrorCode === 409
      ? t('contractReview.errors.inProgress')
      : error?.ErrorCode === 403
        ? t('contractReview.errors.forbidden')
        : errorText(error) ?? (error ? t('contractReview.errors.generic') : undefined);

  const addendumName = (id: number | null | undefined) =>
    id === null || id === undefined ? t('contractReview.history.wholeContract') : props.addendums?.find(a => a.id === id)?.contractAddendumName ?? `#${id}`;
  const selectedBase = baseCandidates.find(a => a.id === baseAddendumId);

  return (
    <>
      {props.showTrigger !== false && (
        <Button
          appearance={isNewAddendum ? 'secondary' : 'primary'}
          icon={isNewAddendum ? <DocumentAdd20Regular /> : <DocumentSearch20Regular />}
          onClick={() => setOpen(true)}
        >
          {isNewAddendum ? t('contractReview.launcher.newAddendum.open') : t('contractReview.launcher.open')}
        </Button>
      )}

      <GuideDrawer
        open={open}
        size="medium"
        eyebrow={props.contractNumber || undefined}
        title={isNewAddendum ? t('contractReview.launcher.newAddendum.title') : t('contractReview.launcher.title')}
        subtitle={isNewAddendum ? t('contractReview.launcher.newAddendum.subtitle') : t('contractReview.launcher.subtitle')}
        onDismiss={() => setOpen(false)}
        closeLabel={t('contractReview.apply.close')}
        preventDismiss={props.isCreating}
        secondaryAction={{ label: t('contractReview.launcher.cancel') }}
        primaryAction={{
          label: t('contractReview.launcher.submit'),
          onClick: submit,
          disabled: !!disabledReason,
          loading: props.isCreating,
          testId: 'review-launcher-submit'
        }}
        testId={`review-drawer-${props.mode}`}
      >
        <GuideDrawerForm autoComplete="off" testId="review-launcher-form">
          {errorMessage && (
            <GuideMessageBar intent="error" role="alert">
              <MessageBarBody>
                <MessageBarTitle>{t('contractReview.errors.title')}</MessageBarTitle>
                {errorMessage}
              </MessageBarBody>
              {error?.ErrorCode === 409 && props.activeReviewId && (
                <MessageBarActions>
                  <Button size="small" onClick={() => props.onViewReview(props.activeReviewId!)}>
                    {t('contractReview.launcher.viewReview')}
                  </Button>
                </MessageBarActions>
              )}
            </GuideMessageBar>
          )}
          {retryOf && (
            <GuideMessageBar intent="info">
              <MessageBarBody>{t('contractReview.launcher.retryLoaded', { date: formatDateTime(retryOf.created) })}</MessageBarBody>
            </GuideMessageBar>
          )}

          {/* ── Adenda ── */}
          {isNewAddendum ? (
            <GuideDrawerSection title={t('contractReview.launcher.newAddendum.base')} description={t('contractReview.launcher.newAddendum.hint')}>
              {baseCandidates.length === 0 ? (
                <GuideHelpText>{t('contractReview.launcher.newAddendum.noBase')}</GuideHelpText>
              ) : (
                <GuideField label={t('contractReview.launcher.newAddendum.base')} required>
                  <Dropdown
                    aria-label={t('contractReview.launcher.newAddendum.base')}
                    value={selectedBase?.contractAddendumName ?? ''}
                    selectedOptions={baseAddendumId !== undefined ? [String(baseAddendumId)] : []}
                    onOptionSelect={(_, data) => setBaseAddendumId(Number(data.optionValue))}
                  >
                    {baseCandidates.map(a => (
                      <Option key={a.id} value={String(a.id)}>
                        {a.contractAddendumName}
                      </Option>
                    ))}
                  </Dropdown>
                </GuideField>
              )}
            </GuideDrawerSection>
          ) : (
            <GuideDrawerSection title={t('contractReview.launcher.addendum')} description={t('contractReview.launcher.addendumHint')}>
              <span data-testid="review-launcher-addendum">{props.addendum?.contractAddendumName ?? t('contractReview.launcher.wholeContract')}</span>
            </GuideDrawerSection>
          )}

          {/* ── Archivos ── */}
          <GuideDrawerSection
            title={t('contractReview.launcher.files')}
            description={t('contractReview.launcher.filesHint')}
            actions={fileIds.length > 0 ? <span className={styles.count}>{fileIds.length}</span> : undefined}
          >
            {props.isLoadingFiles && reviewable.length === 0 ? (
              <GuideSkeleton rows={2} label={t('contractReview.launcher.loadingFiles')} />
            ) : reviewable.length === 0 ? (
              <GuideEmptyState title={t('contractReview.launcher.noPdfs')} text={t('contractReview.launcher.noFilesHint')} />
            ) : (
              <div className={styles.checkList} role="group" aria-label={t('contractReview.launcher.files')}>
                {reviewable.map(file => (
                  <Checkbox
                    key={file.fileId}
                    label={file.fileName}
                    checked={fileIds.includes(file.fileId)}
                    onChange={(_, data) => setFileIds(list => toggle(list, file.fileId, !!data.checked))}
                  />
                ))}
              </div>
            )}
            {fileIds.length === 0 && reviewable.length > 0 && <GuideHelpText>{t('contractReview.launcher.disabledNoFiles')}</GuideHelpText>}
          </GuideDrawerSection>

          {/* ── Qué revisar ── */}
          <GuideDrawerSection title={t('contractReview.launcher.scopes')} description={t('contractReview.launcher.scopesHint')}>
            <div className={styles.checkList} role="group" aria-label={t('contractReview.launcher.scopes')}>
              {ALL_REVIEW_SCOPES.map(scope => (
                <div key={scope} className={styles.scopeOption}>
                  <Checkbox
                    label={t(`contractReview.scope.${scope}`)}
                    checked={scopes.includes(scope)}
                    onChange={(_, data) => setScopes(list => toggle(list, scope, !!data.checked))}
                  />
                  <span className={styles.scopeDescription}>{t(`contractReview.scopeDescription.${scope}`)}</span>
                </div>
              ))}
            </div>
            {scopes.length === 0 && <GuideHelpText>{t('contractReview.launcher.disabledNoScopes')}</GuideHelpText>}
          </GuideDrawerSection>

          {/* ── Instrucciones para la IA (Contracts 021, DEC-026) ── */}
          <GuideDrawerSection title={t('contractReview.instructions.title')} description={t('contractReview.instructions.hint')}>
            <GuideField
              label={t('contractReview.instructions.label')}
              hint={t('contractReview.instructions.help')}
              counter={{ value: instructions.length, max: MAX_REVIEW_INSTRUCTIONS }}
            >
              <Textarea
                rows={3}
                resize="vertical"
                value={instructions}
                maxLength={MAX_REVIEW_INSTRUCTIONS}
                onChange={(_, data) => setInstructions(data.value)}
                placeholder={t('contractReview.instructions.placeholder')}
                data-testid="review-launcher-instructions"
              />
            </GuideField>
          </GuideDrawerSection>

          {/* ── Aplicación automática (Contracts 021 §3.3) ── */}
          <GuideDrawerSection title={t('contractReview.autoApply.title')} description={t('contractReview.autoApply.hint')}>
            <Switch
              checked={autoApply}
              onChange={(_, data) => setAutoApply(!!data.checked)}
              label={autoApply ? t('contractReview.autoApply.on') : t('contractReview.autoApply.off')}
              data-testid="review-launcher-auto-apply"
            />
            <GuideHelpText>{autoApply ? t('contractReview.autoApply.onHelp') : t('contractReview.autoApply.offHelp')}</GuideHelpText>
          </GuideDrawerSection>

          {/* ── Revisiones anteriores ── */}
          <GuideDrawerSection
            title={isNewAddendum ? t('contractReview.launcher.newAddendum.historyTitle') : t('contractReview.launcher.historyTitle')}
            description={t('contractReview.launcher.historyHint')}
            actions={history.length > 0 ? <span className={styles.count}>{history.length}</span> : undefined}
            testId="review-launcher-history"
          >
            {props.isLoadingReviews && history.length === 0 ? (
              <div className={styles.row}>
                <Spinner size="extra-tiny" />
                <span className={styles.secondary}>{t('contractReview.launcher.loadingHistory')}</span>
              </div>
            ) : props.reviewsError && history.length === 0 ? (
              <GuideMessageBar intent="error">
                <MessageBarBody>{t('contractReview.errors.loadHistory')}</MessageBarBody>
                {props.onReloadReviews && (
                  <MessageBarActions>
                    <Button size="small" onClick={props.onReloadReviews}>
                      {t('contractReview.history.retryLoad')}
                    </Button>
                  </MessageBarActions>
                )}
              </GuideMessageBar>
            ) : history.length === 0 ? (
              <p className={styles.secondary}>{t('contractReview.empty.noReviews')}</p>
            ) : (
              <ul className={styles.historyList}>
                {history.map(review => (
                  <li key={review.id} className={styles.historyItem} data-testid={`review-history-${review.id}`}>
                    <div className={styles.spaceBetween}>
                      <div className={styles.row}>
                        <ReviewStatusPill status={review.status} />
                        {review.trigger === 'AfterImport' && <TriggerPill trigger={review.trigger} />}
                        {isAfterImportWithoutChanges(review) && <span className={styles.secondary}>{t('contractReview.panel.noChangesToApply')}</span>}
                        <span className={styles.secondary}>{formatDateTime(review.created)}</span>
                      </div>
                      <div className={styles.row}>
                        {!isReviewActive(review) && review.status !== 'Failed' && (
                          <GuideLinkButton size="small" onClick={() => props.onViewReview(review.id)}>
                            {t('contractReview.history.view')}
                          </GuideLinkButton>
                        )}
                        {canRetry(review) && (
                          <GuideLinkButton size="small" onClick={() => retry(review)}>
                            {t('contractReview.history.retry')}
                          </GuideLinkButton>
                        )}
                      </div>
                    </div>
                    <span className={styles.secondary}>
                      {isNewAddendum
                        ? t('contractReview.history.baseAddendum', { name: addendumName(review.baseAddendumId) })
                        : addendumName(review.addendumId)}
                      {' · '}
                      {review.createdBy}
                      {!isReviewActive(review) && review.status !== 'Failed' && <> · {t('contractReview.history.findingsCount', { count: review.findingCount })}</>}
                    </span>
                    {isReviewActive(review) && (
                      <GuideProgress
                        size="small"
                        value={review.progressPercent}
                        label={review.stage ? t(`contractReview.stage.${review.stage}`, review.stage) : t('contractReview.status.Queued')}
                        ariaLabel={t('contractReview.history.progress', { value: review.progressPercent })}
                      />
                    )}
                    {review.status === 'Failed' && review.errorMessage && <span className={styles.error}>{review.errorMessage}</span>}
                  </li>
                ))}
              </ul>
            )}
          </GuideDrawerSection>
        </GuideDrawerForm>
      </GuideDrawer>
    </>
  );
};

export default ContractReviewLauncher;
